import { useState, createContext, useContext, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useFetchAthleteQuery } from "../../../store";

// Components
import StepIndicator from "./components/StepIndicator";
import Step1GoalSelection from "./steps/Step1GoalSelection";
import BulkConfiguration from "./steps/BulkConfiguration";
import CutConfiguration from "./steps/CutConfiguration";
import MetabolicBuildingConfiguration from "./steps/MetabolicBuildingConfiguration";
import StepSummary from "./steps/StepSummary";

// ============================================
// WIZARD CONTEXT
// ============================================
const WizardContext = createContext(null);

export const useWizard = () => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error("useWizard must be used within a WizardProvider");
  }
  return context;
};

// ============================================
// STEP CONFIGURATION
// ============================================
const GOAL_TYPES = {
  BULK: "bulk",
  CUT: "cut",
  METABOLIC: "metabolic",
};

const STEP_2_COMPONENTS = {
  [GOAL_TYPES.BULK]: BulkConfiguration,
  [GOAL_TYPES.CUT]: CutConfiguration,
  [GOAL_TYPES.METABOLIC]: MetabolicBuildingConfiguration,
};

// ============================================
// MAIN WIZARD COMPONENT
// ============================================
export default function CalendarForm({ onClose, onSubmit }) {
  const [currentStep, setCurrentStep] = useState(1);
  
  const [formData, setFormData] = useState({
    goal: null,
  });

  const totalSteps = 3;

  const stepTitles = [
    "Select Goal",
    "Configuration",
    "Summary",
  ];

  // ============================================
  // NAVIGATION FUNCTIONS
  // ============================================
  
  const goToNextStep = useCallback(() => {
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  }, [totalSteps]);

  const goToPreviousStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  }, []);

  const goToStep = useCallback((step) => {
    if (step >= 1 && step <= totalSteps) {
      setCurrentStep(step);
    }
  }, [totalSteps]);

  const updateFormData = useCallback((newData) => {
    setFormData((prev) => ({ ...prev, ...newData }));
  }, []);

  const resetWizard = useCallback(() => {
    setCurrentStep(1);
    setFormData({ goal: null });
  }, []);

  const handleSubmit = () => {
    console.log("Submitting:", formData);
    if (onSubmit) {
      onSubmit(formData);
    }
    if (onClose) {
      onClose();
    }
  };

  // ============================================
  // RENDER CURRENT STEP
  // ============================================
  
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1GoalSelection />;
      
      case 2:
        const Step2Component = STEP_2_COMPONENTS[formData.goal];
        if (!Step2Component) {
          return (
            <div className="text-center py-12 text-gray-500">
              Please select a goal in Step 1 to continue.
            </div>
          );
        }
        return <Step2Component />;
      
      case 3:
        return <StepSummary />;
      
      default:
        return null;
    }
  };

  // ============================================
  // CONTEXT VALUE
  // ============================================
  
  const wizardContextValue = {
    currentStep,
    totalSteps,
    formData,
    stepTitles,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    updateFormData,
    resetWizard,
    isFirstStep: currentStep === 1,
    isLastStep: currentStep === totalSteps,
    canGoNext: currentStep < totalSteps,
    canGoBack: currentStep > 1,
  };

  // ============================================
  // RENDER
  // ============================================
  
  return (
    <WizardContext.Provider value={wizardContextValue}>
      <div className="space-y-6">
        {/* Step Indicator */}
        <StepIndicator />

        {/* Step Content */}
        <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
          {/* Step Title Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Step {currentStep}: {stepTitles[currentStep - 1]}
            </h2>
          </div>

          {/* Step Content */}
          <div className="p-6 bg-white">
            {renderStep()}
          </div>
        </div>

        {/* Navigation Footer */}
        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
          {/* Back Button */}
          <button
            type="button"
            onClick={goToPreviousStep}
            disabled={currentStep === 1}
            className={`
              px-5 py-2.5 rounded-lg font-medium text-sm transition-all duration-200
              ${currentStep === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200 active:scale-95"
              }
            `}
          >
            ← Back
          </button>

          {/* Step Counter */}
          <span className="text-gray-500 text-sm">
            Step {currentStep} of {totalSteps}
          </span>

          {/* Next/Finish Button */}
          <button
            type="button"
            onClick={currentStep === totalSteps ? handleSubmit : goToNextStep}
            disabled={currentStep === 1 && !formData.goal}
            className={`
              px-5 py-2.5 rounded-lg font-medium text-sm transition-all duration-200
              ${currentStep === 1 && !formData.goal
                ? "bg-indigo-300 text-white cursor-not-allowed"
                : "bg-indigo-600 text-white hover:bg-indigo-500 active:scale-95 shadow-sm"
              }
            `}
          >
            {currentStep === totalSteps ? "Save Configuration" : "Next →"}
          </button>
        </div>
      </div>
    </WizardContext.Provider>
  );
}